import { Box, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAgency } from '@hooks/useAgency';
import { DetailOfficial } from '../types';
import Utils from '../../../utils/utils';
import { PAGE_PATHS } from '../../../constants';
import { UserAvatar } from '@components';
import { useInfo } from '@hooks/useInfo';

type ClearanceDetailSidebarProps = {
  // The scanned official of the selected activity row. The sidebar is closed when null.
  official: DetailOfficial | null;
  onClose: () => void;
};

type DetailRowProps = {
  label: string;
  value: string | null | undefined;
};

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant='body2' sx={{
      color: 'text.secondary'
    }}>{label}</Typography>
    <Typography variant='body2'>{value || '—'}</Typography>
  </Box>
);

const ClearanceDetailSidebar: React.FC<ClearanceDetailSidebarProps> = ({ official, onClose }) => {
  const { agency } = useAgency();
  const { employmentTypeDisplayName } = useInfo();
  const name = official ? Utils.fullName(official?.first_name, official?.last_name) : null;
  const showViewButton = (official?.agency?.id === agency?.id);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 384,
        transform: official ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease',
        bgcolor: 'background.paper',
        boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
        zIndex: 1200,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {official && (
        <>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            px: 3,
            mt: 3,
            mb: 2,
          }}>
            <Box sx={{ display: 'flex' }}>
              <Typography variant='h5'>User Details</Typography>
              {showViewButton && (
                <IconButton
                  sx={{ ml: 1 }}
                  onClick={() => {
                    window.open(`${window.location.origin}${PAGE_PATHS.users}/${official?.id}`, '_blank');
                  }}
                  size='small'
                  aria-label='View user details'
                >
                  <OpenInNewIcon />
                </IconButton>
              )}
            </Box>

            <IconButton
              sx={{ mr: -1 }}
              onClick={onClose}
              size='small'
              aria-label='Close sidebar'
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3 }}>
            <Box sx={{ display: 'flex', mb: 3, mt: 2 }}>
              <UserAvatar
                src={official?.img_url}
                alt={name}
              />
            </Box>

            <DetailRow label='Name' value={name} />
            <DetailRow label='Agency' value={official?.agency?.name} />
            <DetailRow label='Badge' value={official?.badge_number} />
            <DetailRow label='Title' value={official?.title} />
            <DetailRow label='Rank' value={official?.rank} />
            <DetailRow label='Verified' value={Utils.formatBoolean(official?.verified)} />
            <DetailRow label='Email' value={official?.email} />
            <DetailRow label='Phone' value={official?.phone_number} />
            <DetailRow
              label='Designation'
              value={employmentTypeDisplayName(official?.employment_type ?? null) ?? undefined}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClearanceDetailSidebar;
